import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'

interface ColumnWidths {
  [tableKey: string]: {
    [columnKey: string]: number
  }
}

interface ColumnResizeContextValue {
  /** Get width for a column */
  getWidth: (tableKey: string, columnKey: string, defaultWidth: number) => number
  /** Set width for a column */
  setWidth: (tableKey: string, columnKey: string, width: number) => void
  /** Get resize handle props */
  getResizeProps: (tableKey: string, columnKey: string) => {
    onMouseDown: (e: React.MouseEvent) => void
  }
  /** Whether currently resizing */
  isResizing: boolean
  /** Reset all widths for a table */
  resetTable: (tableKey: string) => void
  /** Reset all widths */
  resetAll: () => void
}

const ColumnResizeContext = createContext<ColumnResizeContextValue | null>(null)

const STORAGE_KEY = 'portfolio-column-widths'
const MIN_WIDTH = 20

export function ColumnResizeProvider({ children }: { children: React.ReactNode }) {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef<{
    tableKey: string
    columnKey: string
    startX: number
    startWidth: number
  } | null>(null)

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths))
    } catch {
      // Ignore
    }
  }, [columnWidths])

  const getWidth = useCallback(
    (tableKey: string, columnKey: string, defaultWidth: number): number => {
      return columnWidths[tableKey]?.[columnKey] ?? defaultWidth
    },
    [columnWidths]
  )

  const setWidth = useCallback((tableKey: string, columnKey: string, width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        [columnKey]: Math.max(MIN_WIDTH, width),
      },
    }))
  }, [])

  // Global mouse move/up handlers for resize
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return
      const { tableKey, columnKey, startX, startWidth } = resizeRef.current
      const diff = e.clientX - startX
      const newWidth = Math.max(MIN_WIDTH, startWidth + diff)
      setWidth(tableKey, columnKey, newWidth)
    }

    const handleMouseUp = () => {
      resizeRef.current = null
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
  }, [isResizing, setWidth])

  const getResizeProps = useCallback(
    (tableKey: string, columnKey: string) => ({
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const startWidth = columnWidths[tableKey]?.[columnKey] ?? 100
        resizeRef.current = {
          tableKey,
          columnKey,
          startX: e.clientX,
          startWidth,
        }
        setIsResizing(true)
      },
    }),
    [columnWidths]
  )

  const resetTable = useCallback((tableKey: string) => {
    setColumnWidths((prev) => {
      const next = { ...prev }
      delete next[tableKey]
      return next
    })
  }, [])

  const resetAll = useCallback(() => {
    setColumnWidths({})
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore
    }
  }, [])

  return (
    <ColumnResizeContext.Provider
      value={{ getWidth, setWidth, getResizeProps, isResizing, resetTable, resetAll }}
    >
      {children}
    </ColumnResizeContext.Provider>
  )
}

export function useColumnResize() {
  const context = useContext(ColumnResizeContext)
  if (!context) {
    throw new Error('useColumnResize must be used within a ColumnResizeProvider')
  }
  return context
}
